import { Box, LoadingSpinner, PageGridContainer, SimpleGrid } from "@govlaunch/web";
import React, { useEffect } from "react";
import { IBookmarkType, ICollectionFeaturedStatus, ICollectionPackaging } from "~/types/types";
import ListCollectionsQuery from "~/src/components/collection/graphql/ListCollectionsQuery";
import {
  IListAllCollectionsQuery,
  IListAllCollectionsQueryVariables,
} from "~/src/components/collection/graphql/__generated__/ListCollectionsQuery.generated";
import { NetworkStatus, useQuery, useApolloClient } from "@apollo/client";
import moment from "moment";
import stringOrFirst from "~/src/utils/stringOrFirst";
import CollectionCard from "~/src/components/collection/collectionCard/CollectionCard";
import { ICardViewMode } from "~/src/components/collection/cardViewMode";
import { IToggleBookmarkMutationVariables } from "~/lib/mutations/__generated__/ToggleBookmarkMutation.generated";
import ShowMoreButtonWithIcon from "~/components/form/resources/ShowMoreButtonWithIcon";

interface ISingleCollectionsProps {
  query: string | string[];
  toggleBookmark: (bookmarked: boolean, params: { variables: IToggleBookmarkMutationVariables }) => void;
  isMobile?: boolean;
}

export default function SingleCollections({ query, toggleBookmark, isMobile }: ISingleCollectionsProps) {
  const apollo = useApolloClient();

  useEffect(() => {
    return () => {
      apollo
        .query({
          fetchPolicy: "network-only",
          query: ListCollectionsQuery,
          variables: variables,
        })
        .then((response) => {
          if (!response.data) {
            return;
          }

          apollo.writeQuery({
            query: ListCollectionsQuery,
            variables: variables,
            data: response.data,
          });
        });
    };
  }, []);

  // NOTE: do not touch
  const lastVisitDate = moment().subtract(7, "days").format("YYYY-MM-DD");

  const variables: IListAllCollectionsQueryVariables = {
    filterByType: ICollectionPackaging.Single,
    limit: 12,
    cursor: null,
    searchQuery: stringOrFirst(query),
    lastVisitDate: `${lastVisitDate}T00:00:00Z`,
    filterByFeatured: ICollectionFeaturedStatus.NotFeatured,
  };

  const {
    data: collections,
    loading,
    networkStatus,
    fetchMore,
  } = useQuery<IListAllCollectionsQuery, IListAllCollectionsQueryVariables>(ListCollectionsQuery, {
    notifyOnNetworkStatusChange: true,
    variables,
  });

  if ((loading || networkStatus === NetworkStatus.loading) && !collections?.listAllCollections.items.length) {
    return (
      <Box p="168px" justifyContent="center" display="flex">
        <LoadingSpinner />
      </Box>
    );
  }

  if (!collections || collections.listAllCollections.items.length === 0) {
    return null;
  }

  return (
    <div>
      {(collections.listAllCollections.items.length || 0) > 0 && (
        <Box bg="white" py="36px" mt={5} pb="80px">
          <PageGridContainer px={6}>
            <SimpleGrid columns={[1, 2, 3, 4]} spacingX={[6, 6, 6, "86px"]} spacingY="44px" mr={[0, 0, 0, 12]}>
              {collections?.listAllCollections.items.map((collection, index) => (
                <CollectionCard
                  data-intercom-target={index === 0 ? "First collection" : "Collection card"}
                  key={collection._id}
                  collection={collection}
                  variant={(index % 3) + 1}
                  viewMode={isMobile ? ICardViewMode.Mobile : ICardViewMode.Singles}
                  onToggleBookmark={({ _id }) =>
                    toggleBookmark(!collection.viewerDidBookmark, {
                      variables: {
                        targetId: _id,
                        targetType: IBookmarkType.Collection,
                      },
                    })
                  }
                />
              ))}
            </SimpleGrid>

            {collections.listAllCollections.pageInfo.hasNextPage &&
              [NetworkStatus.ready, NetworkStatus.fetchMore].includes(networkStatus) && (
                <Box display="flex" justifyContent="center" mt={6}>
                  <ShowMoreButtonWithIcon
                    loading={loading}
                    onClick={() => {
                      fetchMore({
                        variables: {
                          cursor: collections.listAllCollections.pageInfo.cursor,
                        },
                        updateQuery: (current, { fetchMoreResult }) => {
                          if (!fetchMoreResult || !fetchMoreResult.listAllCollections || !current.listAllCollections) {
                            return {
                              __typename: "Query",
                              listAllCollections: current.listAllCollections,
                            };
                          }

                          return {
                            __typename: "Query",
                            listAllCollections: {
                              ...current.listAllCollections,
                              items: current.listAllCollections.items.concat(fetchMoreResult.listAllCollections.items),
                              pageInfo: {
                                ...fetchMoreResult.listAllCollections.pageInfo,
                              },
                            },
                          };
                        },
                      });
                    }}
                  />
                </Box>
              )}
          </PageGridContainer>
        </Box>
      )}
    </div>
  );
}
